package com.patronat.top.client.presentation.saved

import androidx.lifecycle.viewModelScope
import com.patronat.top.client.domain.venues.GetClientSavedVenuesCommand
import com.patronat.top.client.domain.venues.GetClientSavedVenuesUseCase
import com.patronat.top.client.domain.venues.UnsaveClientVenueUseCase
import com.patronat.top.core.auth.TokenStorage
import com.patronat.top.core.base.BaseViewModel
import com.patronat.top.core.domain.models.DomainResult
import com.patronat.top.core.presentation.AlertController
import com.patronat.top.core.presentation.NotificationType
import com.patronat.top.core.utils.UiText
import com.patronat.top.dto.VenueResponse
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import top.clientapp.generated.resources.Res
import top.clientapp.generated.resources.client_saved_action_error
import top.clientapp.generated.resources.client_saved_auth_required_message
import top.clientapp.generated.resources.client_saved_auth_required_title
import top.clientapp.generated.resources.client_saved_load_error
import top.design_system.generated.resources.Res as DesignRes
import top.design_system.generated.resources.error_alert_title

class ClientSavedViewModel(
    private val alertController: AlertController,
    private val getSavedVenuesUseCase: GetClientSavedVenuesUseCase,
    private val unsaveVenueUseCase: UnsaveClientVenueUseCase,
    private val tokenStorage: TokenStorage
) : BaseViewModel<ClientSavedViewState, ClientSavedEvent, ClientSavedEffect>(
    initialState = ClientSavedViewState()
) {
    private var searchJob: Job? = null

    override fun onEvent(event: ClientSavedEvent) {
        when (event) {
            ClientSavedEvent.OnRefresh -> {
                searchJob?.cancel()
                loadSavedVenues()
            }
            is ClientSavedEvent.OnSearchChanged -> {
                updateState { it.copy(searchQuery = event.value) }
                searchJob?.cancel()
                searchJob = viewModelScope.launch {
                    delay(SearchDebounceMillis)
                    loadSavedVenues()
                }
            }
            is ClientSavedEvent.OnVenueClicked -> {
                sendEffect(ClientSavedEffect.OpenVenueDetails(event.venueId))
            }
            is ClientSavedEvent.OnFavoriteClicked -> {
                removeSavedVenue(event.venueId)
            }
        }
    }

    private fun loadSavedVenues() {
        viewModelScope.launch {
            val hasToken = !tokenStorage.getAccessToken().isNullOrBlank()
            if (!hasToken) {
                updateState {
                    it.copy(
                        isLoading = false,
                        venues = emptyList(),
                        requiresAuth = true
                    )
                }
                return@launch
            }

            updateState { it.copy(isLoading = true, requiresAuth = false) }

            when (
                val result = getSavedVenuesUseCase.execute(
                    GetClientSavedVenuesCommand(
                        page = 1,
                        pageSize = PageSize,
                        query = currentState.searchQuery.trim().ifBlank { null }
                    )
                )
            ) {
                is DomainResult.Success -> {
                    updateState {
                        it.copy(
                            isLoading = false,
                            venues = result.data.items,
                            requiresAuth = false
                        )
                    }
                }
                is DomainResult.Error -> {
                    updateState { it.copy(isLoading = false) }
                    alertController.show(
                        title = UiText.StringResource(DesignRes.string.error_alert_title),
                        message = result.message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_saved_load_error),
                        type = NotificationType.ERROR
                    )
                }
            }
        }
    }

    private fun removeSavedVenue(venueId: String) {
        val previousVenues = currentState.venues
        val venue = previousVenues.firstOrNull { it.id == venueId } ?: return

        updateState { it.copy(venues = it.venues.filterNot { item -> item.id == venue.id }) }

        viewModelScope.launch {
            val hasToken = !tokenStorage.getAccessToken().isNullOrBlank()
            if (!hasToken) {
                updateState {
                    it.copy(
                        venues = previousVenues,
                        requiresAuth = true
                    )
                }
                alertController.show(
                    title = UiText.StringResource(Res.string.client_saved_auth_required_title),
                    message = UiText.StringResource(Res.string.client_saved_auth_required_message),
                    type = NotificationType.WARNING
                )
                return@launch
            }

            when (val result = unsaveVenueUseCase.execute(venueId)) {
                is DomainResult.Success -> Unit
                is DomainResult.Error -> {
                    updateState { it.copy(venues = previousVenues) }
                    alertController.show(
                        title = UiText.StringResource(DesignRes.string.error_alert_title),
                        message = result.message?.let(UiText::DynamicString)
                            ?: UiText.StringResource(Res.string.client_saved_action_error),
                        type = NotificationType.ERROR
                    )
                }
            }
        }
    }

    private companion object {
        const val PageSize = 100
        const val SearchDebounceMillis = 350L
    }
}
